import { useEffect, useState } from 'react';
import styled from 'styled-components';
import LogoImage from '../../assets/images/logo.png';
import { NavLink, useLocation } from 'react-router-dom';
import produce from 'immer';
import { usePermission } from '../../hooks/usePermission';

const Container = styled.div`
    width: 270px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position-y: bottom;
    background-position-x: right;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    z-index: 1;
    background-color: #016562;
    overflow-y: auto;
`;
const Logo = styled.img`
    width: 93px;
    height: 30px;
    margin: 15px 15px;
`;
const Menus = styled.div`
    text-align: left;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
`;
const Menu = styled(NavLink)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 16px;
    height: 48px;
    cursor: pointer;
    text-decoration: none;

    & > div {
        transition: all 0.3s;
    }

    &.active {
        background-color: #005b57;

        span {
            color: #fff !important;
        }

        & > div {
            transform: rotate(180deg);
        }
    }
`;

const MenuView = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 16px;
    height: 48px;
    cursor: pointer;

    & > div {
        transition: all 0.3s;
    }

    &.active {
        background-color: #005b57 !important;
    }

    &.expand {
        span {
            color: #fff !important;
        }

        & > div {
            transform: rotate(180deg);
        }
    }
`;

const MenuGroup = styled.div`
    height: 48px;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;

    &.expand {
        height: auto;
    }

    &.collapse {
        overflow: inherit;

        & .sub-menu {
            display: none;
        }
    }

    &.collapse:hover .sub-menu {
        display: block;
        position: absolute;
        background-color: #036562;
        left: 46px;
        top: 0;
        width: 150px;
        border-radius: 4px;
        overflow: hidden;
    }
`;
const MenuIcon = styled.span`
    line-height: 15px;
    color: #fff;
    margin-right: 10px;
`;
const MenuText = styled.span`
    font-weight: 700;
    font-size: 16px;
    line-height: 48px;
    color: #f2edd4;
    flex: 1;
    text-decoration: none;
`;
const MenuArrow = styled.div`
    line-height: 20px;
    color: #98989a;

    & > svg {
        width: 15px;
    }
`;
const ArrowIcon = styled.div`
    position: absolute;
    right: 12px;
    top: 21px;
    z-index: 1;
    cursor: pointer;
    transition: all 0.3s;

    & > svg {
        width: 23px;
        transition: all 0.3s;
    }
`;
const MenuLink = styled(NavLink)`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    padding-left: 42px;
    height: 46px;
    width: 100%;
    cursor: pointer;
    font-weight: 400;
    font-size: 14px;
    line-height: 36px;
    color: #f2edd4;
    text-decoration: none;

    //&:before {
    //  content: "";
    //  position: absolute;
    //  left: 32px;
    //  top: 22px;
    //  width: 3px;
    //  height: 3px;
    //  background-color: #F2EDD4;
    //}

    &.active {
        color: #fff;
        background: #307870;
    }
`;

export default function Sidebar({ onExpand, expand }: any) {
    const location = useLocation();
    const [expands, setExpands] = useState<Array<string>>([]);
    const { hasPermission, hasAnyPermission, isSuperAdmin } = usePermission();

    // Permission check helper
    const canAccess = (permissions: string | string[]): boolean => {
        if (isSuperAdmin()) return true;
        if (Array.isArray(permissions)) {
            return hasAnyPermission(permissions);
        }
        return hasPermission(permissions);
    };

    useEffect(() => {
        if (hasActive('user') && !expands.includes('user')) {
            setExpands(
                produce((draft) => {
                    draft.push('user');
                })
            );
        }
        if (hasActive('trail') && !expands.includes('trail')) {
            setExpands(
                produce((draft) => {
                    draft.push('trail');
                })
            );
        }
        if (hasActive('tickets') && !expands.includes('tickets')) {
            setExpands(
                produce((draft) => {
                    draft.push('tickets');
                })
            );
        }
        if (hasActive('exoseed') && !expands.includes('exoseed')) {
            setExpands(
                produce((draft) => {
                    draft.push('exoseed');
                })
            );
        }
    }, [location.pathname, expand]);

    const hasActive = (path: string) => {
        return location.pathname.includes(path);
    };

    const onMenuExpand = (name: string) => {
        if (expands.includes(name)) {
            setExpands(
                produce((draft) => {
                    draft.splice(
                        draft.findIndex((item) => item === name),
                        1
                    );
                })
            );
        } else {
            setExpands(
                produce((draft) => {
                    draft.push(name);
                })
            );
        }
    };

    // Check if Trail menu group should be visible
    const showTrailGroup = canAccess(['trails.view', 'marathon_trails.view', 'stay.view', 'eat_local.view', 'challenges.view', 'reviews.view', 'photos.view']);

    // Check if User menu group should be visible
    const showUserGroup = canAccess(['users.view', 'admins.view', 'roles.view']);

    // Check if Setting menu group should be visible
    const showSettingGroup = canAccess(['settings.ads', 'settings.app_version', 'settings.app_setting', 'settings.attributes', 'settings.notification', 'settings.gps']);

    // Check if Ticketing menu group should be visible
    const showTicketingGroup = canAccess(['tickets.view', 'tickets.my_tickets']);

    return (
        <Container>
            <Logo src={LogoImage} style={{ opacity: expand ? 1 : 0 }} />
            <ArrowIcon onClick={() => onExpand()}>
                <svg style={{ transform: `rotate(${expand ? '0' : '180'}deg)` }} width="25" height="20" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="25" y1="14.75" x2="9" y2="14.75" stroke="#F2EDD4" strokeWidth="1.5" />
                    <line x1="25" y1="7.75" x2="1" y2="7.75" stroke="#F2EDD4" strokeWidth="1.5" />
                    <line x1="25" y1="0.75" x2="1" y2="0.750002" stroke="#F2EDD4" strokeWidth="1.5" />
                    <path d="M6 11L2 15L6 19" stroke="#F2EDD4" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </ArrowIcon>
            <Menus>
                {showTrailGroup && (
                    <MenuGroup className={`${expand && expands?.includes('trail') ? 'expand' : ''} ${!expand ? 'collapse' : ''} ${hasActive('trail') ? 'active' : ''}`}>
                        <MenuView onClick={() => onMenuExpand('trail')} className={`${expand && expands?.includes('trail') ? 'expand' : ''} ${hasActive('trail') ? 'active' : ''}`}>
                            <MenuIcon>
                                <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M13.9644 4.93079C13.9644 4.79611 13.8753 4.66143 13.7507 4.57164L12.5041 3.64385L8.31898 0.755706L7.37511 0.112233C7.1436 -0.0374112 6.82304 -0.0374112 6.60934 0.112233L5.66547 0.755706L1.48039 3.61392L0.215966 4.49682C0.0734955 4.60157 -0.0155508 4.73625 0.00225799 4.90086V5.23008V5.84362V8.95623V11.3505V11.3655L1.48039 12.3681L5.66547 15.2263L6.03945 15.4807V19.177V19.1919V19.2069C6.03945 19.3565 6.09288 19.4912 6.18192 19.6109C6.3422 19.8504 6.66276 20 7.00113 20C7.51759 20 7.92719 19.6558 7.96281 19.2368C7.96281 19.2219 7.96281 19.1171 7.96281 19.1021V15.4807L8.33679 15.2263L12.5219 12.3681L14 11.3655V11.3505V8.95623V5.84362V5.3049L13.9644 4.93079ZM12.3616 10.5574C12.3616 10.722 12.2725 10.8717 12.1301 10.9615L8.94229 13.0116C8.53269 13.266 7.96281 13.0266 7.96281 12.6076V11.2757C7.96281 11.156 8.01623 11.0513 8.08747 10.9615L10.4382 8.41751C10.6341 8.19304 10.5985 7.87879 10.3314 7.69922L9.92178 7.42986C9.65465 7.25028 9.3519 7.29518 9.12038 7.50468C8.69297 7.86382 7.96281 8.97119 7.96281 8.47737V4.48186C7.96281 4.19753 7.69568 3.97306 7.35731 3.97306H6.66276C6.32439 3.97306 6.05726 4.19753 6.05726 4.48186V8.47737C6.05726 8.97119 5.3271 7.92368 4.9175 7.54957C4.68598 7.34007 4.36542 7.23532 4.09829 7.42986L3.68869 7.69922C3.42156 7.87879 3.38594 8.19304 3.59964 8.41751L5.95041 10.9615C6.03945 11.0513 6.07507 11.156 6.07507 11.2757V12.6076C6.07507 13.0415 5.48738 13.281 5.09558 13.0116L1.67629 10.8118V11.0363V5.39469C1.67629 5.23008 1.76533 5.08044 1.90781 4.99065L6.66276 1.89301C6.87647 1.74336 7.19703 1.74336 7.41073 1.89301L12.1657 4.99065C12.3082 5.0954 12.3972 5.24504 12.3972 5.39469L12.3616 10.5574Z"
                                        fill="white"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Trail</MenuText>
                            <MenuArrow style={{ display: expand ? 'block' : 'none' }}>
                                <svg width="17" height="9" viewBox="0 0 17 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5" />
                                </svg>
                            </MenuArrow>
                        </MenuView>
                        <div className={'sub-menu'}>
                            {canAccess('trails.view') && <MenuLink to={'/trail/trails'}>Trails</MenuLink>}
                            {canAccess('marathon_trails.view') && <MenuLink to={'/trail/marathon-trail'}>Marathon trails</MenuLink>}
                            {canAccess('stay.view') && <MenuLink to={'/trail/stay'}>Stay</MenuLink>}
                            {canAccess('eat_local.view') && <MenuLink to={'/trail/eat-local'}>Eat Local</MenuLink>}
                            {canAccess('challenges.view') && <MenuLink to={'/trail/challenges'}>Challenges</MenuLink>}
                            {canAccess('reviews.view') && <MenuLink to={'/trail/reviews'}>Reviews</MenuLink>}
                            {canAccess('photos.view') && <MenuLink to={'/trail/photos'}>Photos</MenuLink>}
                        </div>
                    </MenuGroup>
                )}

                {canAccess('activities.view') && (
                    <MenuGroup>
                        <Menu to={'/activities'}>
                            <MenuIcon>
                                <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M10.7925 14H10.7487C10.5796 13.9905 10.4167 13.9325 10.2796 13.8329C10.1426 13.7333 10.0371 13.5964 9.97573 13.4385L6.05253 3.14191L4.00342 7.65579C3.93365 7.808 3.82168 7.937 3.68079 8.02748C3.5399 8.11796 3.37601 8.16612 3.20857 8.16624H0.875064C0.642983 8.16624 0.420407 8.07405 0.2563 7.90994C0.092194 7.74583 0 7.52326 0 7.29118C0 7.05909 0.092194 6.83652 0.2563 6.67241C0.420407 6.50831 0.642983 6.41611 0.875064 6.41611H2.64707L5.3306 0.509431C5.4025 0.353099 5.51889 0.221434 5.66522 0.130902C5.81155 0.0403709 5.98133 -0.00500877 6.15332 0.000438768C6.3253 0.00588631 6.49187 0.0619193 6.63218 0.16153C6.77249 0.261141 6.88031 0.399908 6.94217 0.560476L10.9018 10.9519L12.9291 6.8974C13.0026 6.75339 13.1142 6.63234 13.2518 6.54747C13.3894 6.46259 13.5477 6.41715 13.7093 6.41611H16.0428C16.2749 6.41611 16.4975 6.50831 16.6616 6.67241C16.8257 6.83652 16.9179 7.05909 16.9179 7.29118C16.9179 7.52326 16.8257 7.74583 16.6616 7.90994C16.4975 8.07405 16.2749 8.16624 16.0428 8.16624H14.249L11.5727 13.5187C11.4992 13.6627 11.3876 13.7838 11.25 13.8686C11.1124 13.9535 10.9541 13.999 10.7925 14Z"
                                        fill="white"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Activities</MenuText>
                            <MenuArrow>
                                {/*<svg width="17" height="9" viewBox="0 0 17 9" fill="none"*/}
                                {/*     xmlns="http://www.w3.org/2000/svg">*/}
                                {/*    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5"/>*/}
                                {/*</svg>*/}
                            </MenuArrow>
                        </Menu>
                    </MenuGroup>
                )}

                {canAccess('ranking.view') && (
                    <MenuGroup>
                        <Menu to={'/ranking'}>
                            <MenuIcon>
                                <svg width="17" height="17" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        fill="#F2EDD4"
                                        d="M62.799,23.737c-0.47-1.399-1.681-2.419-3.139-2.642l-16.969-2.593L35.069,2.265 C34.419,0.881,33.03,0,31.504,0c-1.527,0-2.915,0.881-3.565,2.265l-7.623,16.238L3.347,21.096c-1.458,0.223-2.669,1.242-3.138,2.642 c-0.469,1.4-0.115,2.942,0.916,4l12.392,12.707l-2.935,17.977c-0.242,1.488,0.389,2.984,1.62,3.854 c1.23,0.87,2.854,0.958,4.177,0.228l15.126-8.365l15.126,8.365c0.597,0.33,1.254,0.492,1.908,0.492c0.796,0,1.592-0.242,2.269-0.72 c1.231-0.869,1.861-2.365,1.619-3.854l-2.935-17.977l12.393-12.707C62.914,26.68,63.268,25.138,62.799,23.737z"
                                    ></path>
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Ranking</MenuText>
                            <MenuArrow>
                                {/*<svg width="17" height="9" viewBox="0 0 17 9" fill="none"*/}
                                {/*     xmlns="http://www.w3.org/2000/svg">*/}
                                {/*    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5"/>*/}
                                {/*</svg>*/}
                            </MenuArrow>
                        </Menu>
                    </MenuGroup>
                )}

                {canAccess('membership.view') && (
                    <MenuGroup>
                        <Menu to={'/membership'}>
                            <MenuIcon>
                                <svg width="18" height="18" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        fill="#F2EDD4"
                                        d="M0 26.016q0 0.832 0.576 1.408t1.44 0.576h5.984v-1.984h-0.992q-0.416 0-0.704-0.288t-0.288-0.704 0.288-0.704 0.704-0.32h4q0.384 0 0.704 0.32t0.288 0.704-0.288 0.704-0.704 0.288h-0.992v1.984h12v-1.984h-1.024q-0.384 0-0.704-0.288t-0.288-0.704 0.288-0.704 0.704-0.32h4q0.416 0 0.704 0.32t0.32 0.704-0.32 0.704-0.704 0.288h-0.992v1.984h6.016q0.8 0 1.408-0.576t0.576-1.408v-20q0-0.832-0.576-1.408t-1.408-0.608h-28q-0.832 0-1.44 0.608t-0.576 1.408v20zM4 22.016v-14.016h14.016v14.016h-14.016zM7.040 20h7.936q-1.12-1.472-2.976-1.856v-0.32q0.896-0.352 1.44-1.088t0.576-1.728v-1.984q0-1.248-0.896-2.144t-2.112-0.864-2.144 0.864-0.864 2.144v1.984q0 0.96 0.544 1.728t1.472 1.088v0.32q-1.856 0.384-2.976 1.856zM20 22.016v-2.016h2.016v2.016h-2.016zM20 18.016v-2.016h10.016v2.016h-10.016zM20 14.016v-2.016h4v2.016h-4zM20 10.016v-2.016h6.016v2.016h-6.016zM26.016 14.016v-2.016h4v2.016h-4zM28 10.016v-2.016h2.016v2.016h-2.016z"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Membership</MenuText>
                        </Menu>
                    </MenuGroup>
                )}

                {canAccess('exoseed.view') && (
                    <MenuGroup className={`${expand && expands?.includes('exoseed') ? 'expand' : ''} ${!expand ? 'collapse' : ''} ${hasActive('exoseed') ? 'active' : ''}`}>
                        <MenuView onClick={() => onMenuExpand('exoseed')} className={`${expand && expands?.includes('exoseed') ? 'expand' : ''} ${hasActive('exoseed') ? 'active' : ''}`}>
                            <MenuIcon>
                                <svg width="20" height="30" viewBox="0 0 960 960" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        <path
                                            fill="#F2EDD4"
                                            d="M530.405 500.586C485.98 523.489 473.175 573.351 479.05 618.82C482.947 639.823 495.598 658.785 504.81 677.891C512.487 695.706 511.953 713.055 499.166 728.737C464.573 776.908 384.732 744.32 397.904 681.572C384.515 682.073 375.092 688.74 368.163 698.562C329.782 746.332 357.699 810.454 405.567 839.575C413.277 844.836 424.658 851.661 419.716 862.578C416.985 868.803 411.695 871.018 404.956 870.475C302.052 862.186 225.957 798.881 197.611 699.729C174.372 637.85 198.524 502.352 267.344 480.685C280.821 477.999 291.771 487.71 290.035 501.328C288.603 512.556 284.768 523.592 281.054 534.388C267.644 566.949 300.493 616.604 335.433 594.108C344.375 588.621 349.161 580.766 349.827 570.353C353.475 508.29 289.435 484.64 301.847 415.786C299.383 384.935 358.144 292.504 390.477 314.361C396.028 319.052 397.841 325.365 395.799 332.08C390.315 350.245 378.59 366.81 381.052 386.552C382.677 414.517 420.441 435.513 444.808 421.325C467.988 407.827 488.717 380.445 468.361 345.751C461.094 333.364 454.46 320.607 447.293 308.16C366.163 177.455 561.274 44.0055 566.822 113.011C560.664 145.016 534.329 169.982 532.303 203.566C530.219 215.866 533.247 227.3 539.746 238.021C618.558 349.995 706.545 333.627 644.486 502.23C640.179 515.947 645.215 530.41 662.186 524.816C702.527 513.299 693.207 464.945 695.477 432.634C695.926 425.307 698.564 419.32 706.173 416.961C713.549 414.674 719.541 417.797 722.91 423.868C731.005 438.455 739.228 453.092 745.742 468.414C782.492 554.847 783.915 641.873 748.146 728.98C735.626 759.468 716.435 785.618 690.915 806.409C673.965 820.218 655.897 832.798 637.489 844.62C625.772 852.145 615.684 851.104 610.22 844.23C589.852 823.986 691.375 726.281 618.219 667.388C557.07 625.88 503.776 583.352 530.405 500.586Z"
                                        ></path>
                                    </g>
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>ExoSeed</MenuText>
                            <MenuArrow style={{ display: expand ? 'block' : 'none' }}>
                                <svg width="17" height="9" viewBox="0 0 17 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5" />
                                </svg>
                            </MenuArrow>
                        </MenuView>
                        <div className={'sub-menu'}>
                            <MenuLink to={'/exoseed/campaigns'}>Campaigns</MenuLink>
                            <MenuLink to={'/exoseed/badges'}>Badges</MenuLink>
                            <MenuLink to={'/exoseed/rewards'}>Rewards</MenuLink>
                            <MenuLink to={'/exoseed/stamps'}>Stamps</MenuLink>
                            <MenuLink to={'/exoseed/offers'}>Offers</MenuLink>
                        </div>
                    </MenuGroup>
                )}

                {canAccess('events.view') && (
                    <MenuGroup>
                        <Menu to={'/events'}>
                            <MenuIcon>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        fill="#F2EDD4"
                                        d="M6 2C6 1.44772 6.44772 1 7 1C7.55228 1 8 1.44772 8 2V3H16V2C16 1.44772 16.4477 1 17 1C17.5523 1 18 1.44772 18 2V3H19C20.6569 3 22 4.34315 22 6V20C22 21.6569 20.6569 23 19 23H5C3.34315 23 2 21.6569 2 20V6C2 4.34315 3.34315 3 5 3H6V2ZM4 9V20C4 20.5523 4.44772 21 5 21H19C19.5523 21 20 20.5523 20 20V9H4ZM20 7H4V6C4 5.44772 4.44772 5 5 5H19C19.5523 5 20 5.44772 20 6V7ZM7 12C7 11.4477 7.44772 11 8 11H16C16.5523 11 17 11.4477 17 12C17 12.5523 16.5523 13 16 13H8C7.44772 13 7 12.5523 7 12ZM7 16C7 15.4477 7.44772 15 8 15H12C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17H8C7.44772 17 7 16.5523 7 16Z"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Events</MenuText>
                        </Menu>
                    </MenuGroup>
                )}

                {showTicketingGroup && (
                    <MenuGroup className={`${expand && expands?.includes('tickets') ? 'expand' : ''} ${!expand ? 'collapse' : ''} ${hasActive('tickets') ? 'active' : ''}`}>
                        <MenuView onClick={() => onMenuExpand('tickets')} className={`${expand && expands?.includes('tickets') ? 'expand' : ''} ${hasActive('tickets') ? 'active' : ''}`}>
                            <MenuIcon>
                                <svg fill="#000000" height="17px" width="17px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 459.668 459.668">
                                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        {' '}
                                        <g id="XMLID_2_">
                                            {' '}
                                            <g>
                                                {' '}
                                                <g>
                                                    {' '}
                                                    <path
                                                        fill="#F2EDD4"
                                                        d="M359.574,297.043c-18.204,25.002-47.692,41.286-80.916,41.286h-46.618c-16.104,0-29.818-10.224-35.011-24.534 c-6.41-1.912-12.696-4.394-18.83-7.442c-12.99-6.454-24.785-15.198-35.168-26.03c-67.35,14.796-117.757,74.808-117.757,146.603 v9.384c0,12.9,10.458,23.358,23.358,23.358h362.403c12.9,0,23.358-10.458,23.358-23.358v-9.384 C434.392,371.464,404.309,323.032,359.574,297.043z"
                                                    ></path>{' '}
                                                    <path
                                                        fill="#F2EDD4"
                                                        d="M118.205,232.178c10.039,0,18.777-5.564,23.304-13.775c0.119,0.325,0.24,0.648,0.362,0.971 c0.036,0.097,0.072,0.194,0.108,0.291c10.62,27.954,31.284,51.388,58.532,61.627c6.59-10.471,18.243-17.435,31.53-17.435h46.618 c4.65,0,8.978-1.312,12.772-3.433c6.372-3.563,12.102-12.602,15.061-17.393c4.735-7.667,8.404-15.788,11.657-24.642 c1.828,3.32,4.342,6.208,7.354,8.471v11.431c0,25.83-21.014,46.845-46.845,46.845H232.04c-8.813,0-15.958,7.145-15.958,15.958 c0,8.814,7.145,15.958,15.958,15.958h46.618c43.429,0,78.761-35.332,78.761-78.761V226.86 c6.46-4.853,10.639-12.577,10.639-21.278v-48.119v-18.452c0-8.88-4.355-16.737-11.042-21.568C351.83,51.816,296.77,0,229.833,0 C162.895,0,107.836,51.816,102.65,117.442c-6.687,4.831-11.042,12.689-11.042,21.568v66.57 C91.608,220.311,103.575,232.178,118.205,232.178z M229.833,31.917c49.552,0,90.423,37.868,95.2,86.185 c-3.136,2.467-5.705,5.62-7.475,9.238c-15.058-39.286-48.672-66.638-87.726-66.638c-39.896,0-72.971,28.292-87.667,66.481 c-0.02,0.052-0.039,0.105-0.059,0.158c-1.77-3.618-4.339-6.771-7.475-9.238C139.411,69.785,180.281,31.917,229.833,31.917z"
                                                    ></path>{' '}
                                                </g>{' '}
                                            </g>{' '}
                                        </g>{' '}
                                    </g>
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Ticketing</MenuText>
                            <MenuArrow style={{ display: expand ? 'block' : 'none' }}>
                                <svg width="17" height="9" viewBox="0 0 17 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5" />
                                </svg>
                            </MenuArrow>
                        </MenuView>
                        <div className={'sub-menu'}>
                            {canAccess('tickets.view') && <MenuLink to={'/tickets'}>All tickets</MenuLink>}
                            {canAccess('tickets.my_tickets') && <MenuLink to={'/my-tickets'}>My tickets</MenuLink>}
                        </div>
                    </MenuGroup>
                )}

                {canAccess('crawl.view') && (
                    <MenuGroup>
                        <Menu to={'/crawl'}>
                            <MenuIcon>
                                <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M6.63 15.746L7.21676 16.3311C7.13976 16.4084 7.04825 16.4697 6.94749 16.5116C6.84673 16.5535 6.73869 16.575 6.62959 16.575C6.52048 16.575 6.41244 16.5535 6.31168 16.5116C6.21092 16.4697 6.11942 16.4084 6.04242 16.3311L6.03744 16.3253L6.02336 16.3112L5.97197 16.259C5.67983 15.9581 5.39249 15.6525 5.11007 15.3424C4.40099 14.5655 3.72202 13.7615 3.07466 12.9325C2.33542 11.9794 1.5796 10.902 1.00527 9.83795C0.443381 8.79457 0 7.66417 0 6.6299C0 2.86826 2.97687 0 6.63 0C10.2831 0 13.26 2.86826 13.26 6.6299C13.26 7.66417 12.8166 8.79457 12.2547 9.83712C11.6804 10.9029 10.9254 11.9794 10.1853 12.9325C9.27983 14.0924 8.31266 15.2028 7.28803 16.259L7.23665 16.3112L7.22256 16.3253L7.21758 16.3303L6.63 15.746ZM6.63 9.11612C7.28939 9.11612 7.92178 8.85418 8.38804 8.38792C8.85431 7.92167 9.11625 7.28929 9.11625 6.6299C9.11625 5.97052 8.85431 5.33814 8.38804 4.87189C7.92178 4.40563 7.28939 4.14369 6.63 4.14369C5.97061 4.14369 5.33822 4.40563 4.87196 4.87189C4.40569 5.33814 4.14375 5.97052 4.14375 6.6299C4.14375 7.28929 4.40569 7.92167 4.87196 8.38792C5.33822 8.85418 5.97061 9.11612 6.63 9.11612Z"
                                        fill="#F2EDD4"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Crawl</MenuText>
                            <MenuArrow></MenuArrow>
                        </Menu>
                    </MenuGroup>
                )}

                {showUserGroup && (
                    <MenuGroup className={`${expand && expands?.includes('user') ? 'expand' : ''} ${!expand ? 'collapse' : ''} ${hasActive('user') ? 'active' : ''}`}>
                        <MenuView onClick={() => onMenuExpand('user')} className={`${expand && expands?.includes('user') ? 'expand' : ''} ${hasActive('user') ? 'active' : ''}`}>
                            <MenuIcon>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M3.5 3.31579C3.5 5.14389 5.07033 6.63158 7 6.63158C8.92967 6.63158 10.5 5.14389 10.5 3.31579C10.5 1.48768 8.92967 0 7 0C5.07033 0 3.5 1.48768 3.5 3.31579ZM13.2222 14H14V13.2632C14 10.4197 11.557 8.10526 8.55556 8.10526H5.44444C2.44222 8.10526 0 10.4197 0 13.2632V14H13.2222Z"
                                        fill="white"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>User</MenuText>
                            <MenuArrow style={{ display: expand ? 'block' : 'none' }}>
                                <svg width="17" height="9" viewBox="0 0 17 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5" />
                                </svg>
                            </MenuArrow>
                        </MenuView>
                        <div className={'sub-menu'}>
                            {canAccess('users.view') && <MenuLink to={'/user/customers'}>Customers</MenuLink>}
                            {canAccess('admins.view') && <MenuLink to={'/user/admins'}>Admins</MenuLink>}
                            {canAccess('roles.view') && <MenuLink to={'/user/roles'}>Roles</MenuLink>}
                        </div>
                    </MenuGroup>
                )}

                {showSettingGroup && (
                    <MenuGroup className={`${expand && expands?.includes('setting') ? 'expand' : ''} ${!expand ? 'collapse' : ''} ${hasActive('setting') ? 'active' : ''}`}>
                        <MenuView onClick={() => onMenuExpand('setting')} className={`${expand && expands?.includes('setting') ? 'expand' : ''} ${hasActive('setting') ? 'active' : ''}`}>
                            <MenuIcon>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M6.63264 5.09389C6.16479 5.09389 5.72668 5.2754 5.39496 5.60712C5.06481 5.93883 4.88174 6.37695 4.88174 6.8448C4.88174 7.31264 5.06481 7.75076 5.39496 8.08248C5.72668 8.41263 6.16479 8.5957 6.63264 8.5957C7.10049 8.5957 7.5386 8.41263 7.87032 8.08248C8.20047 7.75076 8.38354 7.31264 8.38354 6.8448C8.38354 6.37695 8.20047 5.93883 7.87032 5.60712C7.7083 5.44385 7.51546 5.31441 7.30298 5.2263C7.09051 5.1382 6.86265 5.09319 6.63264 5.09389ZM13.0839 8.77877L12.0606 7.9041C12.1091 7.60681 12.1341 7.30326 12.1341 7.00127C12.1341 6.69928 12.1091 6.39416 12.0606 6.09843L13.0839 5.22376C13.1612 5.15759 13.2165 5.06945 13.2425 4.97106C13.2685 4.87268 13.264 4.76871 13.2294 4.67299L13.2154 4.63231C12.9337 3.84478 12.5117 3.11479 11.9698 2.47771L11.9417 2.44485C11.8759 2.36748 11.7882 2.31186 11.6901 2.28532C11.5921 2.25879 11.4883 2.26258 11.3925 2.2962L10.1219 2.7484C9.65252 2.36349 9.12991 2.05993 8.56349 1.8487L8.31783 0.520264C8.29929 0.420189 8.25075 0.328122 8.17864 0.256295C8.10653 0.184468 8.01428 0.136281 7.91413 0.118135L7.87189 0.110312C7.05824 -0.0367705 6.20078 -0.0367705 5.38714 0.110312L5.34489 0.118135C5.24474 0.136281 5.15249 0.184468 5.08038 0.256295C5.00828 0.328122 4.95973 0.420189 4.9412 0.520264L4.69397 1.85496C4.13291 2.06788 3.61025 2.37069 3.14648 2.75153L1.86655 2.2962C1.77074 2.26231 1.66688 2.25839 1.56878 2.28494C1.47068 2.31149 1.38298 2.36726 1.31734 2.44485L1.28918 2.47771C0.748247 3.11547 0.32637 3.84527 0.0436726 4.63231L0.0295902 4.67299C-0.0408214 4.86858 0.0170727 5.08764 0.175108 5.22376L1.21094 6.10782C1.16244 6.40199 1.13897 6.70241 1.13897 6.9997C1.13897 7.30013 1.16244 7.60055 1.21094 7.89159L0.178237 8.77564C0.100934 8.84182 0.0456072 8.92996 0.0196136 9.02835C-0.00637996 9.12673 -0.00180874 9.2307 0.0327196 9.32642L0.046802 9.3671C0.330013 10.1541 0.747789 10.8817 1.29231 11.5217L1.32047 11.5546C1.38627 11.6319 1.47397 11.6875 1.57202 11.7141C1.67006 11.7406 1.77384 11.7368 1.86968 11.7032L3.14961 11.2479C3.61589 11.6312 4.13537 11.9348 4.6971 12.1445L4.94433 13.4791C4.96286 13.5792 5.0114 13.6713 5.08351 13.7431C5.15562 13.8149 5.24787 13.8631 5.34802 13.8813L5.39027 13.8891C6.21192 14.037 7.05337 14.037 7.87502 13.8891L7.91726 13.8813C8.01741 13.8631 8.10966 13.8149 8.18177 13.7431C8.25388 13.6713 8.30242 13.5792 8.32096 13.4791L8.56661 12.1507C9.13304 11.9379 9.65565 11.6359 10.1251 11.251L11.3956 11.7032C11.4914 11.7371 11.5953 11.741 11.6934 11.7145C11.7915 11.6879 11.8792 11.6321 11.9448 11.5546L11.973 11.5217C12.5175 10.8786 12.9353 10.1541 13.2185 9.3671L13.2326 9.32642C13.2998 9.1324 13.2419 8.9149 13.0839 8.77877ZM6.63264 9.59555C5.11331 9.59555 3.88189 8.36413 3.88189 6.8448C3.88189 5.32547 5.11331 4.09405 6.63264 4.09405C8.15197 4.09405 9.38339 5.32547 9.38339 6.8448C9.38339 8.36413 8.15197 9.59555 6.63264 9.59555Z"
                                        fill="white"
                                    />
                                </svg>
                            </MenuIcon>
                            <MenuText style={{ display: expand ? 'block' : 'none' }}>Setting</MenuText>
                            <MenuArrow style={{ display: expand ? 'block' : 'none' }}>
                                <svg width="17" height="9" viewBox="0 0 17 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L8.7 8L16.4 1" stroke="#F2EDD4" strokeOpacity="0.5" />
                                </svg>
                            </MenuArrow>
                        </MenuView>
                        <div className={'sub-menu'}>
                            {canAccess('settings.ads') && <MenuLink to={'/setting/ads'}>AD</MenuLink>}
                            {canAccess('settings.app_version') && <MenuLink to={'/setting/app-version'}>App version</MenuLink>}
                            {canAccess('settings.app_setting') && <MenuLink to={'/setting/app-setting'}>App setting</MenuLink>}
                            {canAccess('settings.attributes') && <MenuLink to={'/trail/attributes'}>Attributes</MenuLink>}
                            {canAccess('settings.notification') && <MenuLink to={'/setting/app-notification'}>Notification</MenuLink>}
                            {canAccess('settings.gps') && <MenuLink to={'/setting/gps'}>GPS Settings</MenuLink>}
                        </div>
                    </MenuGroup>
                )}
            </Menus>
        </Container>
    );
}
